import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import HuaweiOnuConsole from './HuaweiOnuConsole';
import {
  ArrowLeft, RefreshCw, Wifi, WifiOff, AlertCircle, CheckCircle,
  Activity, Zap, Clock, MapPin, User, Phone, Hash, Server,
  Settings, RotateCcw, PowerOff, FileText, Eye, EyeOff,
  Signal, Cpu, Network, Edit2, Save, X, ChevronDown, ChevronUp,
  Loader, AlertTriangle, Info, Radio, Globe, Shield
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

const API = window.location.origin;

const T = {
  bg: '#f4f7fb',
  surface: '#ffffff',
  surface1: '#f8fafc',
  surface2: '#eef4fb',
  primary: '#1688d4',
  primaryLight: 'rgba(22,136,212,0.10)',
  success: '#16a05d',
  successBg: 'rgba(22,160,93,0.10)',
  warning: '#d98709',
  warningBg: 'rgba(217,135,9,0.10)',
  error: '#dc4c55',
  errorBg: 'rgba(220,76,85,0.10)',
  onSurface: '#24364b',
  onSurfaceVar: '#718096',
  outline: '#e4eaf2',
  outlineVar: '#d7e0ec',
  accent: '#6b5dd3',
  accentBg: 'rgba(107,93,211,0.10)',
};

function useApi() {
  const token = localStorage.getItem('session_token') || '';
  return useCallback((path, opts = {}) => {
    return fetch(`${API}/api${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(r => r.json());
  }, [token]);
}

const STATUS_CONFIG = {
  online:       { color: T.success, bg: T.successBg, icon: CheckCircle, label: 'Online' },
  offline:      { color: T.error,   bg: T.errorBg,   icon: WifiOff,     label: 'Offline' },
  unconfigured: { color: T.warning, bg: T.warningBg, icon: AlertCircle, label: 'Unconfigured' },
  unknown:      { color: T.onSurfaceVar, bg: T.surface1, icon: AlertCircle, label: 'Unknown' },
};

function StatusBadge({ status, large }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      borderRadius: 20, padding: large ? '6px 14px' : '3px 10px',
      fontSize: large ? 13 : 11, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 5,
      border: `1px solid ${cfg.color}30`,
    }}>
      <Icon size={large ? 14 : 10} />
      {cfg.label}
    </span>
  );
}

function InfoRow({ label, value, mono, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.outline}20` }}>
      <span style={{ color: T.onSurfaceVar, fontSize: 12, fontWeight: 500 }}>{label}</span>
      <span style={{
        color: accent ? T.primary : T.onSurface, fontSize: 12, fontWeight: mono ? 600 : 400,
        fontFamily: mono ? 'monospace' : 'inherit', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-all'
      }}>{value}</span>
    </div>
  );
}

function Card({ title, icon: Icon, children, actions, accent, collapsed: initCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(initCollapsed);
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.outlineVar}`,
      borderRadius: 12, overflow: 'hidden', marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }}>
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${T.outlineVar}`,
          background: accent ? `${T.primaryLight}` : 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {Icon && <Icon size={16} color={accent ? T.primary : T.onSurfaceVar} />}
          <span style={{ color: T.onSurface, fontWeight: 700, fontSize: 13 }}>{title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {actions && !collapsed && <div onClick={e => e.stopPropagation()}>{actions}</div>}
          {collapsed ? <ChevronDown size={14} color={T.onSurfaceVar} /> : <ChevronUp size={14} color={T.onSurfaceVar} />}
        </div>
      </div>
      {!collapsed && <div style={{ padding: '14px 18px' }}>{children}</div>}
    </div>
  );
}

function ActionButton({ label, icon: Icon, onClick, color, loading, disabled, small }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try { await onClick(); } finally { setBusy(false); }
  };
  const isLoading = loading || busy;
  const c = color || T.primary;
  return (
    <button
      onClick={handleClick}
      disabled={isLoading || disabled}
      style={{
        background: `${c}18`, border: `1px solid ${c}40`, borderRadius: 8,
        padding: small ? '6px 12px' : '8px 16px',
        color: c, cursor: isLoading ? 'wait' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: small ? 11 : 12, fontWeight: 600, transition: 'all 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {isLoading ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Icon size={12} />}
      {label}
    </button>
  );
}

function SignalGauge({ value, label, unit = 'dBm', min = -35, max = -5 }) {
  if (value === null || value === undefined) return (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <div style={{ color: T.onSurfaceVar, fontSize: 11 }}>{label}</div>
      <div style={{ color: T.onSurfaceVar, fontSize: 18, fontWeight: 700 }}>—</div>
    </div>
  );
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  const isGood = value >= -27 && value <= -8;
  const isWarn = (value >= -30 && value < -27) || (value > -8 && value <= -5);
  const color = isGood ? T.success : isWarn ? T.warning : T.error;
  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{ color: T.onSurfaceVar, fontSize: 11, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 800, fontFamily: 'monospace' }}>
        {value > 0 ? '+' : ''}{value.toFixed(2)}
        <span style={{ fontSize: 11, fontWeight: 400, color: T.onSurfaceVar }}> {unit}</span>
      </div>
      <div style={{ background: T.surface1, borderRadius: 4, height: 6, margin: '8px 0 4px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.onSurfaceVar }}>
        <span>{min} dBm</span><span>{max} dBm</span>
      </div>
    </div>
  );
}

function MiniChart({ data, dataKey, color, label, unit, height = 80 }) {
  if (!data || data.length === 0) return null;
  const formatTime = (t) => {
    try { return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return t; }
  };
  return (
    <div>
      <div style={{ color: T.onSurfaceVar, fontSize: 11, marginBottom: 4 }}>{label}</div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 2, right: 2, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={T.outline} vertical={false} />
          <XAxis dataKey="time" tickFormatter={formatTime} tick={{ fontSize: 9, fill: T.onSurfaceVar }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: T.onSurfaceVar }} />
          <Tooltip
            contentStyle={{ background: T.surface1, border: `1px solid ${T.outlineVar}`, borderRadius: 8, fontSize: 11 }}
            labelFormatter={formatTime}
            formatter={(v) => [`${v} ${unit}`, label]}
          />
          <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#grad-${dataKey})`} strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function EditPanel({ onu, onSave, onClose }) {
  const [form, setForm] = useState({
    description: onu.description || '',
    odb_splitter: onu.odb_splitter || '',
    zone: onu.zone || '',
    pppoe_username: onu.pppoe_username || '',
  });
  const [saving, setSaving] = useState(false);
  const inp = (label, key, placeholder) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', color: T.onSurfaceVar, fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      <input
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{
          width: '100%', background: T.surface1, border: `1px solid ${T.outlineVar}`,
          borderRadius: 8, padding: '9px 12px', color: T.onSurface, fontSize: 13,
          boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
        }}
      />
    </div>
  );
  return (
    <div style={{ background: T.surface1, borderRadius: 10, padding: 16, border: `1px solid ${T.primary}40` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: T.primary, fontWeight: 700, fontSize: 13 }}>✏️ Edit ONU Info</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.onSurfaceVar, cursor: 'pointer' }}><X size={16} /></button>
      </div>
      {inp('Description / Customer Name', 'description', 'e.g. BGMD003I6')}
      {inp('ODB Splitter', 'odb_splitter', 'e.g. PL-FAT-B07 (Port 10)')}
      {inp('Zone', 'zone', 'e.g. MGLD - Mingalardon Township')}
      {inp('PPPoE Username', 'pppoe_username', 'e.g. user@ml.net')}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '8px 16px', color: T.onSurfaceVar, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
        <button
          onClick={async () => { setSaving(true); await onSave(form); setSaving(false); }}
          disabled={saving}
          style={{ background: T.primary, border: 'none', borderRadius: 8, padding: '8px 20px', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
        >
          {saving ? 'Saving...' : '💾 Save'}
        </button>
      </div>
    </div>
  );
}

export default function ONUDetailPage({ token: tokenProp }) {
  const { onu_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const routeSnapshot = location.state || {};
  const hasPrefetchedDetail = Boolean(routeSnapshot.prefetchedOnu?.id);

  const [onu, setOnu] = useState(() => routeSnapshot.prefetchedOnu || null);
  const [loading, setLoading] = useState(() => !hasPrefetchedDetail);
  const [signalHistory, setSignalHistory] = useState([]);
  const [trafficHistory, setTrafficHistory] = useState([]);
  const [trafficLive, setTrafficLive] = useState(null);
  const [liveStatus, setLiveStatus] = useState(() => routeSnapshot.prefetchedLiveStatus || null);
  const [physicalLinks, setPhysicalLinks] = useState(() => routeSnapshot.prefetchedPhysicalLinks || []);
  const [physicalLoading, setPhysicalLoading] = useState(false);
  const [tr069Status, setTr069Status] = useState(null);
  const [tr069Loading, setTr069Loading] = useState(false);
  const [tr069PushLoading, setTr069PushLoading] = useState(false);
  const [wifiStatus, setWifiStatus] = useState(null);
  const [wifiLoading, setWifiLoading] = useState(false);
  const [wifiError, setWifiError] = useState('');
  const [bandwidth, setBandwidth] = useState(null);
  const [runningConfig, setRunningConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveRunState, setLiveRunState] = useState(null);
  const [liveRunStateLoading, setLiveRunStateLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const autoRefreshRef = useRef(null);
  const liveRunStateIntervalRef = useRef(null);
  const liveRunStateInFlightRef = useRef(false);
  const servicePortRetryRef = useRef(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadOnu = useCallback(async (force = false) => {
    if (!force && hasPrefetchedDetail) return;
    try {
      const data = await api(`/noc/onus/${onu_id}`);
      if (data && data.id) {
        setOnu(data);
        // Seed the console with the fast Radius snapshot; the extended usage
        // endpoint may replace it later when its optional aggregation finishes.
        if (data.detail_radius?.success) setBandwidth(data.detail_radius);
      } else showToast('ONU not found', 'error');
    } catch (e) {
      showToast('Failed to load ONU', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, onu_id]);

  const loadHistories = useCallback(async ({ includeTraffic = true, attempt = 0 } = {}) => {
    // Keep independent read-only data sources isolated. On first render the fast
    // recorded signal and RADIUS snapshots are enough to draw the console; the
    // slower live OLT traffic read is intentionally staged after OLT status.
    const requests = [
      api(`/noc/onus/${onu_id}/signal-history?hours=12`),
      api(`/noc/onus/${onu_id}/bandwidth`),
    ];
    if (includeTraffic) requests.splice(1, 0, api(`/noc/onus/${onu_id}/traffic-history?hours=12`));
    const results = await Promise.allSettled(requests);
    const signalResult = results[0];
    const trafficResult = includeTraffic ? results[1] : null;
    const bandwidthResult = includeTraffic ? results[2] : results[1];
    if (signalResult.status === 'fulfilled') {
      const sig = signalResult.value;
      if (sig && (sig.points || sig.history)) setSignalHistory(sig.points || sig.history || []);
    }
    if (trafficResult?.status === 'fulfilled') {
      const traf = trafficResult.value;
      if (traf && (traf.points || traf.history)) setTrafficHistory(traf.points || traf.history || []);
      if (traf?.live) setTrafficLive(traf.live);
    }
    if (bandwidthResult.status === 'fulfilled' && bandwidthResult.value?.success !== false) {
      setBandwidth(bandwidthResult.value);
    }
    // Retry only the same read-only group once, without creating OLT commands.
    if (attempt < 1 && results.some(result => result.status === 'rejected')) {
      setTimeout(() => loadHistories({ includeTraffic, attempt: 1 }), 1800);
    }
  }, [api, onu_id]);

  const loadTraffic = useCallback(async () => {
    try {
      const traf = await api(`/noc/onus/${onu_id}/traffic-history?hours=12`);
      if (traf && (traf.points || traf.history)) setTrafficHistory(traf.points || traf.history || []);
      if (traf?.live) setTrafficLive(traf.live);
      return traf;
    } catch (error) {
      // Preserve the latest valid rate/sample if this read-only OLT request is transiently unavailable.
      return null;
    }
  }, [api, onu_id]);

  const fetchLiveStatus = React.useCallback(async (silent = false, force = false) => {
    if (!force && routeSnapshot.prefetchedLiveStatus) return routeSnapshot.prefetchedLiveStatus;
    if (!silent) setLiveLoading(true);
    try {
      const res = await api(`/noc/onus/${onu_id}/get-status`, { method: 'POST' });
      setLiveStatus(res);
      if (res.success || res.signal_rx !== null) {
        // Physical Ethernet link is a separate, slower read-only OLT command. Start it only
        // after the fast status result is visible so it never blocks optical/service rendering.
        setPhysicalLoading(true);
        api(`/noc/onus/${onu_id}/ethernet-physical`, { method: 'POST' })
          .then(data => { if (data?.success) setPhysicalLinks(data.ports || []); })
          .catch(() => {})
          .finally(() => setPhysicalLoading(false));
        if (!silent) showToast('Live status fetched from OLT ✓');
        loadOnu();
      } else {
        if (!silent) showToast(res.error || res.optical_error || 'Failed to get live status', 'warning');
      }
    } catch (e) {
      if (!silent) showToast('Failed to get live status', 'warning');
    } finally {
      if (!silent) setLiveLoading(false);
    }
  }, [api, onu_id, loadOnu, routeSnapshot.prefetchedLiveStatus]);
  const handleGetLiveStatus = () => fetchLiveStatus(false, true);

  const fetchLiveRunState = React.useCallback(async () => {
    if (liveRunStateInFlightRef.current) return;
    liveRunStateInFlightRef.current = true;
    setLiveRunStateLoading(true);
    try {
      const res = await api(`/noc/onus/${onu_id}/live-run-state`, { method: 'POST' });
      setLiveRunState(res);
      if (res?.success) {
        setOnu(current => current ? { ...current, status: res.status, last_seen: res.checked_at || current.last_seen } : current);
      }
    } catch (e) {
      // Keep displaying the last verified result if a read-only OLT request temporarily fails.
    } finally {
      liveRunStateInFlightRef.current = false;
      setLiveRunStateLoading(false);
    }
  }, [api, onu_id]);

  const loadTr069Status = React.useCallback(async (autoPushIfNeeded = false) => {
    if (!onu_id) return;
    setTr069Loading(true);
    try {
      const res = await api(`/noc/onus/${onu_id}/tr069-status`, { method: 'GET' });
      setTr069Status(res);
      // Auto-push TR-069 ACS URL via OMCI if not registered
      if (autoPushIfNeeded && res && !res.registered) {
        try {
          const pushRes = await api(`/noc/onus/${onu_id}/push-tr069`, { method: 'POST' });
          if (pushRes && pushRes.success) {
            // Re-check status after 15s (ONU needs time to reboot/apply)
            setTimeout(async () => {
              try {
                const updated = await api(`/noc/onus/${onu_id}/tr069-status`, { method: 'GET' });
                setTr069Status(updated);
              } catch (e3) {}
            }, 15000);
          }
        } catch (e2) { /* silent auto-push failure */ }
      }
    } catch (e) {
      setTr069Status({ registered: false, error: e.message });
    } finally {
      setTr069Loading(false);
    }
  }, [api, onu_id]);

  const wifiEligible = Boolean(wifiStatus?.eligible);
  const loadWifiStatus = React.useCallback(async () => {
    if (!onu_id) return null;
    setWifiLoading(true);
    try {
      const result = await api(`/noc/onus/${onu_id}/wifi-clients`);
      setWifiStatus(result || null);
      setWifiError(result?.eligible ? '' : (result?.message || 'Wi-Fi client telemetry is unavailable.'));
      return result;
    } catch (error) {
      setWifiStatus({ eligible: false, availability: 'unavailable', message: 'Unavailable — unable to load read-only Wi-Fi telemetry.', clients: [] });
      setWifiError('Unavailable — unable to load read-only Wi-Fi telemetry.');
      return null;
    } finally { setWifiLoading(false); }
  }, [api, onu_id]);

  const queueLegacyTestWifiPassword = React.useCallback(async (password, confirmation) => {
    if (String(onu?.onu_serial || '').toUpperCase() !== '48575443DC91E99A') {
      return { error: 'This legacy test-password workflow is restricted to its original test ONU.' };
    }
    try {
      const result = await api('/noc/tr069/wifi-test-hg8546m/password', { method: 'POST', body: { password, confirmation } });
      if (result?.success) { showToast('Wi-Fi password task queued for the next Inform ✓'); setTimeout(loadWifiStatus, 700); }
      return result;
    } catch (error) { return { error: 'Unable to queue the legacy test Wi-Fi password task.' }; }
  }, [api, onu?.onu_serial, loadWifiStatus]);

  useEffect(() => {
    loadOnu();
    // Render baseline ERP, recorded optical history, and RADIUS data immediately.
    // Do not let the first live counter command take the OLT read lock ahead of
    // the status/optical panel that operators need first.
    loadHistories({ includeTraffic: false });
    // Read TR-069 status only during normal page load. The explicit Push ACS control
    // remains available; provisioning commands must not compete with the live OLT read.
    loadTr069Status(false);
    loadWifiStatus();

    let trafficTimer;
    // Start the one required live OLT status read first. Traffic begins only after
    // it settles, so service-port lookup can reuse its verified result instead of
    // queuing competing SSH/Telnet work during initial console rendering.
    const statusTimer = hasPrefetchedDetail && routeSnapshot.prefetchedLiveStatus
      ? setTimeout(async () => {
        // A list snapshot is enough to render the detail immediately; refresh the
        // OLT status only after the screen is visible and never block navigation.
        if (routeSnapshot.prefetchedNeedsLiveRefresh) await fetchLiveStatus(true, true);
        trafficTimer = setTimeout(() => { loadTraffic(); }, 1200);
      }, routeSnapshot.prefetchedNeedsLiveRefresh ? 650 : 0)
      : setTimeout(async () => {
        await fetchLiveStatus(true);
        trafficTimer = setTimeout(() => { loadTraffic(); }, 1200);
      }, 400);

    // The status response already supplies initial run state. A later lightweight
    // recheck preserves automatic monitoring without delaying the first view.
    const runStateTimer = setTimeout(() => { fetchLiveRunState(); }, 60000);
    liveRunStateIntervalRef.current = setInterval(() => { fetchLiveRunState(); }, 60000);
    const trafficInterval = setInterval(() => { loadTraffic(); }, 60000);
    // ERP metadata refresh only; it issues no OLT CLI command.
    autoRefreshRef.current = setInterval(() => { loadOnu(); }, 60000);
    const wifiTimer = setInterval(() => { loadWifiStatus(); }, 30000);
    return () => { clearInterval(autoRefreshRef.current); clearInterval(liveRunStateIntervalRef.current); clearInterval(wifiTimer); clearInterval(trafficInterval); clearTimeout(statusTimer); clearTimeout(runStateTimer); clearTimeout(trafficTimer); };
  }, [loadOnu, loadHistories, loadTr069Status, loadWifiStatus, loadTraffic, fetchLiveStatus, fetchLiveRunState, hasPrefetchedDetail, routeSnapshot.prefetchedLiveStatus, routeSnapshot.prefetchedNeedsLiveRefresh]);



  // Huawei detail output can return the general ONT state before its service-port page.
  // Retry once only; this preserves automatic status loading without fabricating a port value.
  useEffect(() => {
    if (!liveStatus?.success || liveStatus.status !== 'online' || liveStatus.service_port || servicePortRetryRef.current) return undefined;
    servicePortRetryRef.current = true;
    const retryTimer = setTimeout(() => fetchLiveStatus(true), 4500);
    return () => clearTimeout(retryTimer);
  }, [liveStatus, fetchLiveStatus]);

  const pushTr069Config = React.useCallback(async () => {
    setTr069PushLoading(true);
    try {
      const res = await api(`/noc/onus/${onu_id}/push-tr069`, { method: 'POST' });
      if (res.success) {
        showToast('TR-069 ACS URL pushed to ONU via OMCI ✓');
        // Wait 5s then re-check TR-069 status
        setTimeout(() => loadTr069Status(), 5000);
      } else {
        showToast(res.error || res.message || 'TR-069 push may have failed', 'warning');
      }
      return res;
    } catch (e) {
      showToast('Failed to push TR-069 config: ' + e.message, 'error');
    } finally {
      setTr069PushLoading(false);
    }
  }, [api, onu_id, loadTr069Status]);
  const handleGetRunningConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await api(`/noc/onus/${onu_id}/get-running-config`, { method: 'POST' });
      setRunningConfig(res);
      setShowConfig(true);
      if (!res.success) showToast(res.error || 'Failed to get config', 'warning');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleReboot = async () => {
    if (!window.confirm(`Reboot ONU ${onu?.onu_serial}? This will disconnect the customer briefly.`)) return;
    const res = await api(`/noc/onus/${onu_id}/reboot`, { method: 'POST' });
    if (res.success) showToast('Reboot command sent ✓');
    else showToast(res.error || 'Reboot failed', 'error');
  };

  const handleDisable = async () => {
    if (!window.confirm(`Disable ONU ${onu?.onu_serial}? This will disconnect the customer.`)) return;
    const res = await api(`/noc/onus/${onu_id}/disable`, { method: 'POST' });
    if (res.success) { showToast('ONU disabled ✓'); loadOnu(); }
    else showToast(res.error || 'Disable failed', 'error');
  };

  const handleResync = async () => {
    try {
      const res = await api(`/noc/olts/${onu?.olt_id}/sync`, { method: 'POST', body: JSON.stringify({ method: 'telnet', reason: 'manual_onu_resync' }) });
      if (res.success) showToast('OLT bulk resync completed ✓');
      else showToast(res.error || 'Resync failed', 'error');
    } catch (error) { showToast(error.message || 'Resync failed', 'error'); }
  };

  const handleSave = async (form) => {
    const linkChanged = ['pppoe_username', 'customer_username', 'dma_service_id', 'speed_profile_id', 'zone', 'odb_splitter']
      .some(key => String(onu?.[key] ?? '') !== String(form?.[key] ?? ''));
    const res = await api(`/noc/onus/${onu_id}`, { method: 'PUT', body: form });
    if (res.success || res.id) {
      showToast('ONU updated ✓');
      setEditing(false);
      loadOnu();
      if (linkChanged && onu?.olt_id) {
        const shouldResync = window.confirm('ONU/PPPoE link ပြောင်းထားပါတယ်။ သက်ဆိုင်ရာ OLT ကို autoresync လုပ်မလား?\n\nYes = bulk read-only sync\nNo = link update ကိုပဲ သိမ်းထားမယ်');
        if (shouldResync) {
          try {
            const sync = await api(`/noc/olts/${onu.olt_id}/sync`, { method: 'POST', body: JSON.stringify({ method: 'telnet', reason: 'onu_link_changed' }) });
            if (sync.success) showToast('OLT autoresync completed ✓');
            else showToast(sync.error || 'OLT autoresync failed', 'error');
          } catch (error) { showToast(error.message || 'OLT autoresync failed', 'error'); }
        }
      }
    } else {
      showToast('Update failed', 'error');
    }
  };

  if (loading) return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <Loader size={32} color={T.primary} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ color: T.onSurfaceVar, marginTop: 12, fontSize: 13 }}>Loading ONU details...</div>
      </div>
    </div>
  );

  if (!onu) return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: T.onSurfaceVar }}>
        <AlertCircle size={48} color={T.error} />
        <div style={{ marginTop: 12 }}>ONU not found</div>
        <button onClick={() => navigate(-1)} style={{ marginTop: 16, background: T.primary, border: 'none', borderRadius: 8, padding: '8px 20px', color: '#fff', cursor: 'pointer' }}>Go Back</button>
      </div>
    </div>
  );

  const statusCfg = STATUS_CONFIG[onu.status] || STATUS_CONFIG.unknown;
  const operational = liveStatus?.operational || {};
  const liveServicePort = liveStatus?.service_port || null;
  const isBridgeMode = /bridge/i.test(String(onu.onu_mode || '')) || /bridge/i.test(String(onu.description || ''));
  const serviceConfigured = isBridgeMode ? Boolean(onu.vlan_id) : Boolean(onu.pppoe_username && onu.vlan_id);
  const radiusOnline = Boolean(bandwidth?.session?.is_online);
  const serviceLabel = isBridgeMode ? (liveServicePort?.state === 'up' ? 'VLAN bridge handoff active' : 'Bridge handoff configured') : !serviceConfigured ? 'Subscriber service required' : radiusOnline ? 'PPPoE session online' : 'PPPoE configured — awaiting session';



  return (
    <HuaweiOnuConsole
      onu={onu}
      liveStatus={liveStatus}
      physicalLinks={physicalLinks}
      physicalLoading={physicalLoading}
      operational={operational}
      liveServicePort={liveServicePort}
      bandwidth={bandwidth}
      signalHistory={signalHistory}
      trafficHistory={trafficHistory}
      trafficLive={trafficLive}
      tr069Status={tr069Status}
      tr069Loading={tr069Loading}
      tr069PushLoading={tr069PushLoading}
      wifiStatus={wifiStatus}
      wifiLoading={wifiLoading}
      wifiError={wifiError}
      wifiEligible={wifiEligible}
      runningConfig={runningConfig}
      showConfig={showConfig}
      toast={toast}
      liveLoading={liveLoading}
      liveRunState={liveRunState}
      liveRunStateLoading={liveRunStateLoading}
      configLoading={configLoading}
      isBridgeMode={isBridgeMode}
      serviceLabel={serviceLabel}
      radiusOnline={radiusOnline}
      onBack={() => navigate(-1)}
      onConfig={handleGetRunningConfig}
      onReboot={handleReboot}
      onDisable={handleDisable}
      onResync={handleResync}
      onPushTr069={pushTr069Config}
      onRefreshWifi={loadWifiStatus}
      onQueueWifiPassword={queueLegacyTestWifiPassword}
    />
  );
}
